import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { AuraMark, AuraWordmark } from "@/components/AuraMark";
import { SplitEditor } from "@/components/SplitEditor";
import { DistributionDialog } from "@/components/DistributionDialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import {
  LayoutGrid, DollarSign, Gauge, Activity, ShieldAlert, PieChart, LogOut, ArrowLeft,
  TrendingUp, Users, Coins, AlertTriangle, Database, Sparkles, Trash2, Receipt, Download, Landmark, Send,
} from "lucide-react";

const ORANGE = "#C8A15C", VIOLET = "#B0863C", CYAN = "#E4C88C";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : n);
const money = (n) => `$${fmt(n)}`;

const Kpi = ({ label, value, sub, color = "#fff", testid }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
    data-testid={testid} className="rounded-xl p-5 border border-[#222430] bg-[#14151C] h-full">
    <p className="label-mono text-[#71717A]">{label}</p>
    <p className="font-display font-extrabold text-2xl mt-2" style={{ color }}>{value}</p>
    {sub && <p className="text-xs text-[#A1A1AA] mt-1">{sub}</p>}
  </motion.div>
);

const Panel = ({ title, children, right, testid }) => (
  <div data-testid={testid} className="rounded-2xl p-6 border border-[#222430] bg-[#14151C]">
    <div className="flex items-center justify-between mb-5">
      <h3 className="font-display font-semibold text-[#fff]">{title}</h3>{right}
    </div>
    {children}
  </div>
);

const tooltipStyle = { background: "#0D0E12", border: "1px solid #222430", borderRadius: 10, color: "#fff", fontSize: 12 };

export default function Admin() {
  const { user, logout, underConstruction, setUnderConstruction } = useAuth();
  const nav = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [mode, setMode] = useState("executive");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [payoutPeriod, setPayoutPeriod] = useState("");

  const endpoints = {
    executive: "/admin/executive", revenue: "/admin/revenue", subscriptions: "/admin/subscriptions",
    margins: "/admin/margins", operations: "/admin/operations", security: "/admin/security",
    credits: "/admin/credits", splits: "/admin/splits", production: "/admin/production",
    reserve: "/admin/reserve", distributions: "/admin/distributions",
  };

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const keys = m === "executive" ? ["executive"] : m === "revenue" ? ["revenue", "subscriptions"]
        : m === "margin" ? ["margins", "credits"] : m === "operations" ? ["operations"]
        : m === "production" ? ["production"] : m === "economics" ? ["margins", "revenue"]
        : m === "retention" ? ["subscriptions", "credits", "executive"]
        : m === "security" ? ["security"] : m === "splits" ? ["splits"]
        : m === "reserve" ? ["reserve", "distributions"] : ["executive"];
      const res = {};
      for (const k of keys) { res[k] = (await api.get(endpoints[k])).data; }
      setData((d) => ({ ...d, ...res }));
    } catch (e) { toast.error("Failed to load cockpit data"); }
    setLoading(false);
  }, []);

  useEffect(() => { load(mode); }, [mode, load]);

  const loadPayouts = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/payouts${p ? `?period=${p}` : ""}`);
      setData((d) => ({ ...d, payouts: res.data }));
      setPayoutPeriod(res.data.period);
    } catch (e) { toast.error("Failed to load payout statements"); }
    setLoading(false);
  }, []);

  useEffect(() => { if (mode === "payouts") loadPayouts(); }, [mode, loadPayouts]);

  const downloadStatements = () => {
    const p = data.payouts; if (!p) return;
    const rows = [["Period", "Recipient", "Type", "Percentage", "Amount (USD)"]];
    rows.push([p.period, "Platform (retained)", "platform", p.platform_retained.percentage, p.platform_retained.amount]);
    (p.statements || []).forEach((s) => rows.push([p.period, s.recipient_email, s.recipient_type, s.percentage, s.amount]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `aurasession-payouts-${p.period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const [emailing, setEmailing] = useState(false);
  const emailStatements = async () => {
    const p = data.payouts; if (!p) return;
    if (!window.confirm(`Email the ${p.period} statement to every contractor (excludes reserved slots)? This sends real emails.`)) return;
    setEmailing(true);
    try {
      const res = await api.post(`/admin/payouts/email?period=${p.period}`);
      toast.success(`Emailed ${res.data.sent_count} statement(s) for ${p.period}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send statements"); }
    setEmailing(false);
  };

  const seedDemo = async () => { await api.post("/admin/demo/seed"); toast.success("Demo dataset generated"); load(mode); };
  const clearDemo = async () => { await api.post("/admin/demo/clear"); toast.success("Demo data cleared"); load(mode); };

  const toggleConstruction = async (val) => {
    try {
      await api.post("/admin/site/under-construction", { enabled: val });
      setUnderConstruction(val);
      toast.success(val
        ? "Under construction ON — public visitors see the pre-launch page; admins keep full access"
        : "Site is now LIVE to the public");
    } catch (e) { toast.error("Could not update site status"); }
  };

  const modes = [
    { id: "executive", label: "Executive", icon: LayoutGrid },
    { id: "production", label: "Production", icon: Activity },
    { id: "economics", label: "Economics", icon: Gauge },
    { id: "retention", label: "Retention", icon: DollarSign },
    { id: "revenue", label: "Revenue", icon: DollarSign },
    { id: "margin", label: "Usage & Margin", icon: Gauge },
    { id: "operations", label: "Operations", icon: Activity },
    { id: "security", label: "Security", icon: ShieldAlert },
    ...(isSuper ? [{ id: "splits", label: "Splits", icon: PieChart, super: true }] : []),
    ...(isSuper ? [{ id: "payouts", label: "Payouts", icon: Receipt, super: true }] : []),
    ...(isSuper ? [{ id: "reserve", label: "Reserve", icon: Landmark, super: true }] : []),
  ];

  const ex = data.executive || {};

  return (
    <div className="min-h-screen" style={{ background: "#050507" }}>
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-[#222430]" style={{ background: "rgba(5,5,7,0.85)", backdropFilter: "blur(16px)" }}>
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AuraMark className="w-8 h-8" />
            <div><AuraWordmark className="text-base" /><span className="ml-2 label-mono text-orange">Cockpit</span></div>
          </div>
          <div className="flex items-center gap-3">
            <span className="label-mono px-3 py-1.5 rounded-full text-orange bg-orange-500/10 border border-orange-500/20">{isSuper ? "SUPER ADMIN" : "ADMIN"}</span>
            <span className="text-xs text-[#71717A] hidden sm:block">Fresh {ex.freshness ? new Date(ex.freshness).toLocaleTimeString() : ", "}</span>
            <Button data-testid="admin-back-btn" onClick={() => nav("/dashboard")} variant="ghost" size="sm" className="text-[#A1A1AA]"><ArrowLeft className="w-4 h-4 mr-1" /> Studio</Button>
            <button data-testid="admin-logout" onClick={logout} className="text-[#A1A1AA] hover:text-red-400"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </header>

      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-6">
        {/* mode switcher */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {modes.map((m) => (
            <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors border ${
                mode === m.id ? "text-white border-transparent" : "text-[#A1A1AA] border-[#222430] hover:text-white"}`}
              style={mode === m.id ? { background: m.super ? VIOLET : ORANGE } : {}}>
              <m.icon className="w-4 h-4" /> {m.label}
            </button>
          ))}
          {isSuper && (
            <div className="ml-auto flex items-center gap-3">
              <div data-testid="under-construction-toggle" className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#222430] bg-[#14151C]">
                <span className={`w-2 h-2 rounded-full ${underConstruction ? "bg-orange-400" : "bg-emerald-400"}`} />
                <span className="label-mono text-xs text-[#A1A1AA]">{underConstruction ? "UNDER CONSTRUCTION" : "LIVE"}</span>
                <Switch data-testid="under-construction-switch" checked={!!underConstruction} onCheckedChange={toggleConstruction} />
              </div>
              <Button data-testid="seed-demo-btn" onClick={seedDemo} size="sm" className="rounded-full bg-[#14151C] border border-[#222430] text-[#A1A1AA] hover:text-white"><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Generate demo data</Button>
              <Button data-testid="clear-demo-btn" onClick={clearDemo} size="sm" variant="ghost" className="rounded-full text-[#71717A]"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Clear</Button>
            </div>
          )}
        </div>

        {ex.demo_active && mode === "executive" && (
          <div className="mb-5 flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-violet-500/30 bg-violet-500/5" style={{ color: VIOLET }}>
            <Database className="w-3.5 h-3.5" /> DEMO DATASET ACTIVE, figures include clearly-labeled demo records. Clear anytime.
          </div>
        )}

        {/* alerts */}
        {mode === "executive" && (ex.alerts || []).map((a, i) => (
          <div key={i} className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg border"
            style={{ borderColor: a.level === "critical" ? ORANGE : VIOLET, background: "rgba(255,85,0,0.06)", color: "#fff" }}>
            <AlertTriangle className="w-4 h-4" style={{ color: a.level === "critical" ? ORANGE : VIOLET }} /> {a.message}
          </div>
        ))}

        {loading && <div className="py-20 text-center text-[#71717A]">Loading cockpit…</div>}

        {/* PRODUCTION */}
        {!loading && mode === "production" && (() => { const p = data.production || {}; return (
          <div className="space-y-6" data-testid="production-view">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Kpi testid="kpi-success" label="Build success" value={`${p.success_rate ?? 0}%`} color={CYAN} sub={`${p.jobs_completed || 0}/${p.jobs_total || 0} builds`} />
              <Kpi testid="kpi-fail" label="Failure rate" value={`${p.failure_rate ?? 0}%`} color={ORANGE} sub={`${p.jobs_failed || 0} failed`} />
              <Kpi testid="kpi-running" label="In flight" value={fmt(p.jobs_running)} sub={`${p.jobs_canceled || 0} canceled`} />
              <Kpi testid="kpi-projects" label="Projects" value={fmt(p.projects)} sub={`${p.dna_generated || 0} with DNA`} />
              <Kpi testid="kpi-clips" label="Clips made" value={fmt(p.clips_generated)} color={VIOLET} />
              <Kpi testid="kpi-packages" label="Packages built" value={fmt(p.packages_built)} />
            </div>
            <Panel title="Where compute goes (credits by group)" testid="panel-prod-spend">
              <div className="space-y-2">
                {(p.spend_by_group || []).map((g) => (
                  <div key={g.group} className="flex items-center justify-between text-sm border-b border-[#222430] py-2">
                    <span className="text-[#A1A1AA] capitalize">{g.group}</span>
                    <span className="text-[#71717A]">{g.count} ops · <span className="text-white">{fmt(g.credits)} cr</span></span>
                  </div>
                ))}
                {!(p.spend_by_group || []).length && <p className="text-[#71717A] text-sm">No production activity yet.</p>}
              </div>
            </Panel>
          </div>
        ); })()}

        {/* ECONOMICS */}
        {!loading && mode === "economics" && (() => { const m = data.margins || {}; const r = data.revenue || {}; return (
          <div className="space-y-6" data-testid="economics-view">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi testid="kpi-cm" label="Contribution margin" value={`${Math.round((m.contribution_margin || 0) * 100)}%`} color={(m.contribution_margin || 0) >= 0.65 ? CYAN : ORANGE} sub={`Floor 65%`} />
              <Kpi testid="kpi-netrev" label="Net revenue" value={money(m.net_revenue)} sub={`Var cost ${money(m.variable_cost)}`} />
              <Kpi testid="kpi-gross" label="Gross revenue" value={money((r.gross_revenue ?? r.gross) || m.net_revenue)} />
              <Kpi testid="kpi-floor" label="Floor status" value={(m.margin_floor_status || "ok").toUpperCase()} color={(m.margin_floor_status === "ok") ? CYAN : ORANGE} />
            </div>
            <Panel title="Margin by operation" testid="panel-econ-ops">
              <table className="w-full text-sm"><thead><tr className="text-left label-mono text-[#52525B] border-b border-[#222430]"><th className="pb-2">Operation</th><th className="pb-2 text-right">Revenue</th><th className="pb-2 text-right">Cost</th><th className="pb-2 text-right">Margin</th></tr></thead>
                <tbody>{(m.by_operation || []).map((o, i) => (
                  <tr key={i} className="border-b border-[#1a1b22]"><td className="py-2 text-[#A1A1AA]">{o.operation || o.label}</td><td className="py-2 text-right text-white">{money(o.revenue)}</td><td className="py-2 text-right text-[#71717A]">{money(o.cost)}</td><td className="py-2 text-right" style={{ color: (o.margin || 0) >= 0.65 ? CYAN : ORANGE }}>{Math.round((o.margin || 0) * 100)}%</td></tr>
                ))}</tbody></table>
              {!(m.by_operation || []).length && <p className="text-[#71717A] text-sm">No economics data yet.</p>}
            </Panel>
          </div>
        ); })()}

        {/* RETENTION */}
        {!loading && mode === "retention" && (() => { const s = data.subscriptions || {}; const ex2 = data.executive || {}; return (
          <div className="space-y-6" data-testid="retention-view">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi testid="kpi-active" label="Active subs" value={fmt(ex2.active_subscribers ?? s.active)} color={CYAN} />
              <Kpi testid="kpi-churn" label="Churn" value={`${ex2.churn_rate ?? s.churn_rate ?? 0}%`} color={ORANGE} />
              <Kpi testid="kpi-arpu" label="ARPU" value={money(ex2.arpu)} />
              <Kpi testid="kpi-ltv" label="Est. LTV" value={money((ex2.arpu || 0) * (ex2.churn_rate ? 100 / ex2.churn_rate : 12))} sub="ARPU ÷ churn" />
            </div>
            <Panel title="Subscribers by plan" testid="panel-ret-plans">
              <div className="space-y-2">
                {(s.by_plan || []).map((pl) => (
                  <div key={pl.plan || pl.plan_id} className="flex items-center justify-between text-sm border-b border-[#222430] py-2">
                    <span className="text-[#A1A1AA]">{pl.plan || pl.plan_id}</span><span className="text-white">{fmt(pl.count)}</span>
                  </div>
                ))}
                {!(s.by_plan || []).length && <p className="text-[#71717A] text-sm">No subscriber data yet.</p>}
              </div>
            </Panel>
          </div>
        ); })()}


        {/* EXECUTIVE */}
        {!loading && mode === "executive" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Kpi testid="kpi-mrr" label="MRR" value={money(ex.mrr)} color={ORANGE} sub={`ARR ${money(ex.arr)}`} />
              <Kpi testid="kpi-net" label="Net revenue" value={money(ex.net_revenue)} color={CYAN} sub={`Gross ${money(ex.gross_revenue)}`} />
              <Kpi testid="kpi-subs" label="Active subs" value={fmt(ex.active_subscribers)} sub={`Churn ${ex.churn_rate}%`} />
              <Kpi testid="kpi-arpu" label="ARPU" value={money(ex.arpu)} />
              <Kpi testid="kpi-credits" label="Credits sold" value={fmt(ex.credits_sold)} sub={`Used ${fmt(ex.credits_consumed)}`} color={VIOLET} />
              <Kpi testid="kpi-margin" label="Contribution margin" value={`${Math.round((ex.contribution_margin || 0) * 100)}%`}
                color={ex.margin_floor_status === "ok" ? CYAN : ORANGE} sub={`Floor ${Math.round((ex.margin_floor || 0.65) * 100)}%`} />
            </div>
            <Panel title="Net revenue, last 30 days" testid="panel-revenue-trend">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={ex.revenue_trend || []}>
                  <defs><linearGradient id="og" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={ORANGE} stopOpacity={0.5} /><stop offset="100%" stopColor={ORANGE} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1c1e28" />
                  <XAxis dataKey="date" stroke="#52525B" fontSize={11} /><YAxis stroke="#52525B" fontSize={11} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="revenue" stroke={ORANGE} strokeWidth={2} fill="url(#og)" />
                </AreaChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        )}

        {/* REVENUE */}
        {!loading && mode === "revenue" && data.revenue && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Subscription rev" value={money(data.revenue.subscription_revenue)} color={ORANGE} />
              <Kpi label="Credit-pack rev" value={money(data.revenue.credit_pack_revenue)} color={CYAN} />
              <Kpi label="Payment fees" value={money(data.revenue.fees)} />
              <Kpi label="Net revenue" value={money(data.revenue.net_revenue)} color={VIOLET} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Panel title="Revenue by plan" testid="panel-rev-plan">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.revenue.revenue_by_plan || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1c1e28" />
                    <XAxis dataKey="plan" stroke="#52525B" fontSize={11} /><YAxis stroke="#52525B" fontSize={11} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                    <Bar dataKey="revenue" radius={[6,6,0,0]}>{(data.revenue.revenue_by_plan||[]).map((e,i)=><Cell key={i} fill={[ORANGE,VIOLET,CYAN][i%3]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>
              <Panel title="Subscriptions by status" testid="panel-subs">
                <div className="space-y-3 mt-2">
                  {Object.entries(data.subscriptions?.counts || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between"><span className="label-mono text-[#A1A1AA]">{k}</span><span className="font-display font-bold text-white">{v}</span></div>
                  ))}
                </div>
              </Panel>
            </div>
          </div>
        )}

        {/* MARGIN */}
        {!loading && mode === "margin" && data.margins && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Contribution margin" value={`${Math.round((data.margins.contribution_margin||0)*100)}%`} color={data.margins.contribution_margin >= data.margins.margin_floor ? CYAN : ORANGE} />
              <Kpi label="Floor" value={`${Math.round(data.margins.margin_floor*100)}%`} color={ORANGE} />
              <Kpi label="Variable cost" value={money(data.margins.variable_cost)} />
              <Kpi label="Credits consumed" value={fmt(data.credits?.consumed)} color={VIOLET} sub={`Utilization ${data.credits?.utilization_rate}%`} />
            </div>
            <Panel title="Margin by operation" testid="panel-margin-op">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left label-mono text-[#52525B] border-b border-[#222430]"><th className="pb-2">Operation</th><th className="pb-2 text-right">Count</th><th className="pb-2 text-right">Revenue</th><th className="pb-2 text-right">Cost</th><th className="pb-2 text-right">Margin</th></tr></thead>
                  <tbody>
                    {(data.margins.by_operation||[]).map((o,i)=>(
                      <tr key={i} className="border-b border-[#181a24]">
                        <td className="py-2 text-[#E4E4E7]">{o.operation}</td><td className="py-2 text-right text-[#A1A1AA]">{o.count}</td>
                        <td className="py-2 text-right text-[#A1A1AA]">{money(o.revenue)}</td><td className="py-2 text-right text-[#A1A1AA]">${o.cost}</td>
                        <td className="py-2 text-right font-mono-a" style={{ color: o.margin == null ? "#52525B" : (o.margin >= 0.65 ? CYAN : ORANGE) }}>{o.margin == null ? ", " : `${Math.round(o.margin*100)}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        )}

        {/* OPERATIONS */}
        {!loading && mode === "operations" && data.operations && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Kpi label="Total jobs" value={fmt(data.operations.jobs_total)} />
              <Kpi label="Completed" value={fmt(data.operations.jobs_completed)} color={CYAN} />
              <Kpi label="Failed" value={fmt(data.operations.jobs_failed)} color={ORANGE} />
              <Kpi label="Running" value={fmt(data.operations.jobs_running)} color={VIOLET} />
              <Kpi label="Success rate" value={`${data.operations.success_rate}%`} />
            </div>
            <Panel title="Operations breakdown" testid="panel-ops">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.operations.operations_breakdown || []} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1c1e28" />
                  <XAxis type="number" stroke="#52525B" fontSize={11} /><YAxis type="category" dataKey="operation" width={140} stroke="#52525B" fontSize={10} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                  <Bar dataKey="count" fill={VIOLET} radius={[0,6,6,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>
        )}

        {/* SECURITY */}
        {!loading && mode === "security" && data.security && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Failed logins" value={fmt(data.security.failed_logins)} color={ORANGE} />
            <Kpi label="Revoked devices" value={fmt(data.security.revoked_devices)} />
            <Kpi label="Webhook failures" value={fmt(data.security.webhook_failures)} color={ORANGE} />
            <Kpi label="Queue backlog" value={fmt(data.security.queue_backlog)} color={VIOLET} />
            <Kpi label="API error rate" value={`${data.security.api_error_rate}%`} />
            <Kpi label="DB health" value={data.security.db_health} color={CYAN} />
            <Kpi label="Storage health" value={data.security.storage_health} color={CYAN} />
          </div>
        )}

        {/* SPLITS (super only) */}
        {!loading && mode === "splits" && data.splits && (
          <div className="space-y-6">
            <div className="rounded-lg px-4 py-3 border border-[#222430] bg-[#14151C] text-xs text-[#A1A1AA] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: VIOLET }} /> Super-admin only. The platform-retained share and the 65% contribution-margin target are separate financial layers. Edit the rule to add recipients or fill open partner slots.
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Kpi label="Net distributable" value={money(data.splits.net_distributable)} color={ORANGE} />
              <Kpi label="Active rule" value={data.splits.active_rule?.version_name || ", "} color={VIOLET} />
              <Kpi label="Recipients" value={(data.splits.allocations || []).length} />
            </div>
            <Panel title="Revenue split allocations" testid="panel-splits"
              right={<SplitEditor splits={data.splits} onSaved={() => load("splits")} />}>
              <div className="space-y-3">
                {(data.splits.allocations || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="text-[#E4E4E7] text-sm w-64 truncate">{a.recipient}</span>
                    <div className="flex-1 h-3 rounded-full bg-black/40 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${a.percentage}%`, background: a.type === "platform" ? ORANGE : VIOLET }} />
                    </div>
                    <span className="label-mono text-[#A1A1AA] w-12 text-right">{a.percentage}%</span>
                    <span className="font-display font-bold text-white w-20 text-right">{money(a.amount)}</span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        )}

        {!loading && mode === "payouts" && data.payouts && (() => { const p = data.payouts; return (
          <div className="space-y-6" data-testid="payouts-view">
            <div className="rounded-lg px-4 py-3 border border-[#222430] bg-[#14151C] text-xs text-[#A1A1AA] flex items-center gap-2">
              <Receipt className="w-4 h-4" style={{ color: VIOLET }} /> Super-admin only. Monthly earnings statement per recipient = net distributable revenue for the month × their active split share. Reserved (open) slots are shown as unallocated. Admins are Stripe Connect contractors and file their own taxes.
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="label-mono text-[#71717A]">Statement period</label>
              <select data-testid="payout-period-select" value={payoutPeriod} onChange={(e) => loadPayouts(e.target.value)}
                className="bg-[#14151C] border border-[#222430] text-white text-sm rounded-full px-4 py-2">
                {(p.periods.length ? p.periods : [p.period]).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <Button data-testid="payout-download-btn" onClick={downloadStatements} size="sm" className="rounded-full font-semibold ml-auto" style={{ background: VIOLET }}>
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download statements (CSV)
              </Button>
              <Button data-testid="payout-email-btn" onClick={emailStatements} disabled={emailing} size="sm" className="rounded-full font-semibold" style={{ background: ORANGE, color: "#0D0E12" }}>
                <Send className="w-3.5 h-3.5 mr-1.5" /> {emailing ? "Emailing…" : "Email statements"}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Gross revenue" value={money(p.gross_revenue)} sub={`${p.transactions} transactions`} />
              <Kpi label="Processor fees" value={money(p.fees)} color="#FF5500" />
              <Kpi label="Net distributable" value={money(p.net_distributable)} color={ORANGE} />
              <Kpi label="Platform retained" value={money(p.platform_retained.amount)} sub={`${p.platform_retained.percentage}%`} color={VIOLET} />
            </div>
            <Panel title={`Recipient statements — ${p.period}`} testid="panel-payouts">
              <table className="w-full text-sm">
                <thead><tr className="text-left label-mono text-[#52525B] border-b border-[#222430]">
                  <th className="pb-2">Recipient</th><th className="pb-2">Type</th>
                  <th className="pb-2 text-right">Share</th><th className="pb-2 text-right">Earnings</th>
                </tr></thead>
                <tbody>
                  {(p.statements || []).map((s, i) => (
                    <tr key={i} data-testid={`payout-row-${i}`} className={`border-b border-[#222430]/60 ${s.reserved ? "opacity-50" : ""}`}>
                      <td className="py-2.5 text-[#E4E4E7]">{s.recipient_email}</td>
                      <td className="py-2.5"><span className="label-mono text-xs px-2 py-0.5 rounded-full border border-[#222430] text-[#A1A1AA]">{s.recipient_type}</span></td>
                      <td className="py-2.5 text-right label-mono text-[#A1A1AA]">{s.percentage}%</td>
                      <td className="py-2.5 text-right font-display font-bold text-white">{s.reserved ? <span className="text-[#71717A] font-normal">unallocated</span> : money(s.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        ); })()}

        {!loading && mode === "reserve" && data.reserve && (() => { const r = data.reserve; const L = r.lifetime || {}; return (
          <div className="space-y-6" data-testid="reserve-view">
            <div className="rounded-lg px-4 py-3 border border-[#222430] bg-[#14151C] text-xs text-[#A1A1AA] flex items-center gap-2">
              <Landmark className="w-4 h-4" style={{ color: VIOLET }} /> Super-admin only. The retained share ({r.retained_percentage}% = platform + reserved slots) funds operating costs, reserves and growth. Distributable = retained revenue − operating costs. Quarterly profit distributions to the two principal partners are discretionary and not guaranteed.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Retained revenue (lifetime)" value={money(L.retained_revenue)} sub={`${r.retained_percentage}% of net`} color={VIOLET} />
              <Kpi label="Operating costs" value={money(L.operating_costs)} color="#FF5500" />
              <Kpi label="Distributed to date" value={money(L.distributed)} />
              <Kpi label="Reserve balance" value={money(L.reserve_balance)} color={ORANGE} />
            </div>
            <Panel title="Quarterly distributable profit" testid="panel-reserve"
              right={<DistributionDialog reserve={r} onSaved={() => load("reserve")} />}>
              {(r.quarters || []).length === 0 ? (
                <p className="text-sm text-[#71717A]">No revenue recorded yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left label-mono text-[#52525B] border-b border-[#222430]">
                    <th className="pb-2">Quarter</th><th className="pb-2 text-right">Net revenue</th>
                    <th className="pb-2 text-right">Retained</th><th className="pb-2 text-right">Op. costs</th>
                    <th className="pb-2 text-right">Distributable</th><th className="pb-2 text-right">Distributed</th>
                    <th className="pb-2 text-right">Remaining</th>
                  </tr></thead>
                  <tbody>
                    {(r.quarters || []).map((q, i) => (
                      <tr key={i} data-testid={`reserve-row-${i}`} className="border-b border-[#222430]/60">
                        <td className="py-2.5 text-[#E4E4E7]">{q.period}</td>
                        <td className="py-2.5 text-right text-[#A1A1AA]">{money(q.net_revenue)}</td>
                        <td className="py-2.5 text-right text-[#A1A1AA]">{money(q.retained_revenue)}</td>
                        <td className="py-2.5 text-right text-[#A1A1AA]">{money(q.operating_costs)}</td>
                        <td className="py-2.5 text-right font-display font-bold" style={{ color: CYAN }}>{money(q.distributable)}</td>
                        <td className="py-2.5 text-right text-[#A1A1AA]">{money(q.distributed)}</td>
                        <td className="py-2.5 text-right font-display font-bold text-white">{money(q.remaining)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
            <Panel title="Distribution history" testid="panel-distributions">
              {(data.distributions || []).length === 0 ? (
                <p className="text-sm text-[#71717A]">No distributions declared yet.</p>
              ) : (
                <div className="space-y-3">
                  {(data.distributions || []).map((d, i) => (
                    <div key={i} data-testid={`distribution-row-${i}`} className="rounded-lg border border-[#222430] bg-black/20 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span className="font-display font-semibold text-white">{d.period}</span>
                        <span className="font-display font-bold" style={{ color: CYAN }}>{money(d.declared_amount)}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#A1A1AA]">
                        {(d.allocations || []).map((a, j) => <span key={j}>{a.email}: {money(a.amount)}</span>)}
                      </div>
                      {d.reason && <p className="mt-1.5 text-xs text-[#71717A]">{d.reason}</p>}
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        ); })()}
      </div>
    </div>
  );
}
